import React, { MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import { Footer } from '~/common/Footer'
import { AppHeader } from '~/common/AppHeader'
import { Button, Modal } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery } from '@apollo/client'
import {
  AddChannelToSummitDocument,
  GetManageSummitLivestreamLayoutDocument,
  GetReleaseLayoutDocument,
  GetSummitLivestreamChannelsDocument,
  GetSummitLivestreamMetricsDocument,
  RemoveSummitChannelDocument,
  RenameSummitChannelDocument,
} from '~/api/generated/graphql'
import { VideoPlayer } from '~/common/VideoPlayer'
import PlainTextInput from '~/common/PlainTextInput'
import pencilIcon from '@web/images/community/pencil-icon.svg'
import '@css/pages/page/SummitLivestreams.scss'
import { asDate } from '~/utils'
import MoreArrow from '@web/images/community/MoreArrow'
import LessArrow from '@web/images/community/LessArrow'
import ToastComponent from '~/common/ToastComponent'

type ChannelWithMetricsType = {
  channelId: string
  name: string
  metrics?: {
    state: string
    health: string
    viewerCount: string
    ingestBitrateMbps: string
    keyframeIntervalS: string
  }
  livestreamKey: string
  livestreamPlayback: string | null
}
type SummitEventType = {
  eventId: string
  eventStart?: Date
  eventEnd?: Date
  running: boolean
  community?: {
    __typename?: 'Community' | undefined
    communityId?: string | null | undefined
    name: string
  } | null
  livestreamChannelId?: string | null
  title?: string | null
}

const handleExpanderClicked: MouseEventHandler<HTMLAnchorElement> = e => {
  let target: Element | null = e.target as Element
  while (target && target instanceof Element) {
    if (target instanceof Element) {
      if (target.classList.contains('expander-container')) {
        target.classList.toggle('collapsed')
        target.classList.toggle('expanded')
        break
      }
      target = target.parentElement
    }
  }
}

export const SummitLivestreams = () => {
  const { releaseId } = useParams<{ releaseId: string }>()
  const canEditPosts = true
  const { data } = useQuery(GetManageSummitLivestreamLayoutDocument, {
    variables: { id: releaseId ?? '', filter: canEditPosts ? {} : { draft: { eq: false } } },
    skip: !releaseId,
    fetchPolicy: 'cache-and-network',
  })
  const { data: releaseLayoutData } = useQuery(GetReleaseLayoutDocument, {
    variables: { id: releaseId ?? '', filter: canEditPosts ? {} : { draft: { eq: false } } },
    skip: !releaseId,
    fetchPolicy: 'cache-and-network',
  })

  const navigate = useNavigate()
  const [addChannelToSummit] = useMutation(AddChannelToSummitDocument)
  const [removeSummitChannel] = useMutation(RemoveSummitChannelDocument)
  const [renameSummitChannel] = useMutation(RenameSummitChannelDocument)
  const [showAddChannelModal, setShowAddChannelModal] = useState(false)
  const [addChannelErrorMessage, setAddChannelErrorMessage] = useState('')
  const [addingChannel, setAddingChannel] = useState(false)
  const [channelName, setChannelName] = useState('')
  const [showRenameChannelModal, setShowRenameChannelModal] = useState(false)
  const [renameChannelErrorMessage, setRenameChannelErrorMessage] = useState('')
  const [showDeleteChannelModal, setShowDeleteChannelModal] = useState(false)
  const [deleteChannelErrorMessage, setDeleteChannelErrorMessage] = useState('')
  const [renamingChannel, setRenamingChannel] = useState(false)
  const [deletingChannel, setDeletingChannel] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  const handleClickCopyVendor = (_: React.MouseEvent) => {
    const permalink = data?.release?.livestreamVendorUrl ?? ''
    void navigator.clipboard?.writeText(permalink).then(() => setToastMessage('Link to summit copied to clipboard.'))
  }
  const clickSubmitAddChannel = useCallback(() => {
    setAddingChannel(true)
    addChannelToSummit({ variables: { releaseId: releaseId ?? '', name: channelName } }).then(
      () => {
        setAddingChannel(false)
        setShowAddChannelModal(false)
      },
      error_ => {
        setAddingChannel(false)
        setAddChannelErrorMessage(error_)
      }
    )
  }, [releaseId, channelName, setAddingChannel, setShowAddChannelModal, setAddChannelErrorMessage, addChannelToSummit])
  const clickAddChannel = useCallback(() => {
    setChannelName('')
    setAddChannelErrorMessage('')
    setShowAddChannelModal(true)
  }, [setShowAddChannelModal, setAddChannelErrorMessage, setChannelName])

  const { data: livestreamChannelsData } = useQuery(GetSummitLivestreamChannelsDocument, {
    variables: { id: releaseId ?? '' },
    skip: !releaseId,
    fetchPolicy: 'network-only',
  })

  const {
    data: livestreamMetricsData,
    startPolling: pollMetrics,
    stopPolling: stopPollMetrics,
  } = useQuery(GetSummitLivestreamMetricsDocument, {
    variables: { id: releaseId ?? '' },
    skip: !releaseId,
    fetchPolicy: 'network-only',
  })
  const [activeChannelId, setActiveChannelId] = useState('')
  const livestreamChannelsById = useMemo(() => {
    return Object.groupBy(livestreamChannelsData?.release?.channels ?? [], channel => channel?.channelId ?? '')
  }, [livestreamChannelsData])

  const eventsByChannel = useMemo(() => {
    const ret = {} as Record<string, SummitEventType[]>
    for (const channelId of Object.keys(livestreamChannelsById)) {
      ret[channelId] = [] as SummitEventType[]
    }
    const now = new Date()
    const tenMinutesFromNow = new Date()
    tenMinutesFromNow.setMinutes(now.getMinutes() + 10)

    const allEvents = [] as SummitEventType[]
    for (const catEdge of releaseLayoutData?.release?.categories?.edges ?? []) {
      for (const eventEdge of catEdge?.node?.events?.edges ?? []) {
        if (eventEdge?.node) {
          const eventStart = asDate(eventEdge.node.eventStart)
          allEvents.push({
            ...eventEdge?.node,
            running: Boolean(eventStart && eventStart < tenMinutesFromNow),
            eventStart: eventStart,
            eventEnd: asDate(eventEdge.node.eventEnd),
          })
        }
      }
    }

    allEvents.sort((a, b) => {
      const aStart = a.eventStart ?? now
      const bStart = b.eventStart ?? now
      const aEnd = a.eventEnd ?? now
      const bEnd = b.eventEnd ?? now
      if (aStart < bStart || (aStart == bStart && aEnd < bEnd)) {
        return -1
      }
      if (aStart > bStart || (aStart == bStart && aEnd > bEnd)) {
        return 1
      }
      return 0
    })
    for (const event of allEvents) {
      if (event.livestreamChannelId && event.eventEnd && event.eventEnd > now) {
        ret[event.livestreamChannelId]?.push(event)
      }
    }

    return ret
  }, [releaseLayoutData, livestreamChannelsById])

  const clickRenameChannel = useCallback(
    (channelId: string) => {
      const channel = livestreamChannelsById[channelId]?.[0]
      if (channel) {
        setActiveChannelId(channelId)
        setChannelName(channel?.name ?? '')
        setShowRenameChannelModal(true)
      }
    },
    [livestreamChannelsById]
  )
  const clickDeleteChannel = useCallback(
    (channelId: string) => {
      const channel = livestreamChannelsById[channelId]?.[0]
      if (channel) {
        setActiveChannelId(channel.channelId ?? '')
        setChannelName(channel?.name ?? '')
        setShowDeleteChannelModal(true)
      }
    },
    [livestreamChannelsById]
  )
  const clickSubmitDeleteChannel = useCallback(() => {
    setDeletingChannel(true)
    removeSummitChannel({ variables: { channelId: activeChannelId } }).then(
      () => {
        setDeletingChannel(false)
        setShowDeleteChannelModal(false)
      },
      error_ => {
        setDeleteChannelErrorMessage(error_)
        setDeletingChannel(false)
      }
    )
  }, [setDeleteChannelErrorMessage, setShowDeleteChannelModal, activeChannelId, removeSummitChannel])

  const clickSubmitRenameChannel = useCallback(() => {
    setRenamingChannel(true)
    renameSummitChannel({ variables: { channelId: activeChannelId, channelName: channelName } }).then(
      r => {
        if (r.data?.renameSummitChannel?.error) {
          setRenameChannelErrorMessage(r.data?.renameSummitChannel?.error?.message ?? '')
          setRenamingChannel(false)
        } else {
          setRenamingChannel(false)
          setShowRenameChannelModal(false)
        }
      },
      error_ => {
        setRenamingChannel(false)
        setRenameChannelErrorMessage(error_)
      }
    )
  }, [setRenameChannelErrorMessage, setShowRenameChannelModal, channelName, activeChannelId, renameSummitChannel])

  useEffect(() => {
    if (releaseId && livestreamChannelsData) {
      console.log('polling')
      pollMetrics(10000)
      return () => {
        stopPollMetrics()
      }
    }
  }, [pollMetrics, stopPollMetrics, releaseId, livestreamChannelsData])
  const metricsByChannel = useMemo(() => {
    const metrics = Object.groupBy(livestreamMetricsData?.release?.channels ?? [], channel => channel?.channelId ?? '')
    const ret = [] as ChannelWithMetricsType[]
    for (const channel of livestreamChannelsData?.release?.channels ?? []) {
      if (channel?.channelId) {
        ret.push((metrics[channel?.channelId]?.[0] ?? channel) as ChannelWithMetricsType)
      }
    }
    return ret
  }, [livestreamChannelsData, livestreamMetricsData])
  const release = data?.release
  const clickDone = useCallback(() => {
    void navigate(`/summits/${release?.releaseId}`)
  }, [release, navigate])
  return (
    <div className={'summit-livestreams'}>
      <AppHeader />
      <div className={`add-page-wrapper `}>
        <div className={'add-page-container'}>
          <div className={'add-page-header'}>
            <h3 className="comm-title">{release?.title}</h3>
            <div className={'buttons-container'}>
              <Button onClick={clickAddChannel} variant="primary">
                Add Channel
              </Button>
            </div>

            <div className={'buttons-container'}>
              <Button onClick={clickDone} variant="primary">
                Done
              </Button>
            </div>
          </div>
          <div>Status: {release?.draft ? 'Draft' : 'Published'}</div>
          <div>
            External Viewer URL:{' '}
            <button
              className={'btn btn-link vendor-url'}
              hidden={!release?.livestreamVendorUrl}
              onClick={handleClickCopyVendor}
            >
              {release?.livestreamVendorUrl ?? ''}
            </button>
          </div>
          <div className={'channel-box'}>
            {metricsByChannel?.map(channel => (
              <div className={'metrics-box'} key={channel?.channelId}>
                <div className={'channel-header'}>
                  <h3>{channel?.name}</h3>
                  <div className={'buttons-container'}>
                    <button className={'rename-button'} onClick={() => clickRenameChannel(channel.channelId)}>
                      <img alt={'edit'} src={pencilIcon} data-testid={'edit-pencil'} />
                    </button>
                    <button className={'delete-button'} onClick={() => clickDeleteChannel(channel.channelId)}>
                      X
                    </button>
                  </div>
                </div>
                <p>Health: {channel?.metrics?.health}</p>
                <p>State: {channel?.metrics?.state}</p>
                <p>Viewer Count: {channel?.metrics?.viewerCount}</p>
                <p>Key: {channel?.livestreamKey}</p>
                <p>Bitrate: {channel?.metrics?.ingestBitrateMbps}</p>
                <p>Keyframe: {channel?.metrics?.keyframeIntervalS}</p>
                {eventsByChannel[channel.channelId].length > 0 && (
                  <div className={'expander-container collapsed'}>
                    <a
                      href={'#'}
                      className={'expander-header'}
                      onClick={handleExpanderClicked}
                      tabIndex={0}
                      data-testid={'expander-header-' + channel.channelId}
                    >
                      <span className="expander-more">
                        <MoreArrow fill="#565656" width={'15'} height={'15'} />
                      </span>
                      <span className="expander-less">
                        <LessArrow fill="#565656" width={'15'} height={'15'} />
                      </span>
                      <h3 className={'members-h3'}>Events Streaming to this Channel</h3>
                    </a>
                    <div className="expander-body">
                      {eventsByChannel[channel.channelId].map(event => {
                        return (
                          <div className={`summit-event-row ${event.running ? 'running' : ''}`} key={event?.eventId}>
                            <div className={'event-community'}>
                              <a
                                href={`/communities/${event?.community?.communityId}/events/${event?.eventId}`}
                                target="_blank"
                              >
                                {event?.community?.name}
                              </a>
                            </div>
                            <div className={'event-title'}>
                              <a
                                href={`/communities/${event?.community?.communityId}/events/${event?.eventId}`}
                                target="_blank"
                              >
                                {event.title}
                              </a>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                <p>
                  {Boolean(channel?.livestreamPlayback) && 'LIVE' == channel?.metrics?.state ? (
                    <VideoPlayer muted={true} className="video" src={channel?.livestreamPlayback ?? ''} />
                  ) : (
                    <></>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
      <Modal show={showAddChannelModal} onHide={() => setShowAddChannelModal(false)} className={''}>
        <Modal.Header closeButton />
        <Modal.Body>
          <p className={'message'}>{addChannelErrorMessage}</p>
          <div className={'release-info-input channel-name'}>
            <label>Channel Name</label>
            <PlainTextInput
              placeholder={'Channel Name'}
              onChange={s => {
                setChannelName(s.slice(0, 200))
              }}
              value={channelName}
              className={'channel-name-input'}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={() => setShowAddChannelModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => clickSubmitAddChannel()} disabled={addingChannel}>
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showRenameChannelModal} onHide={() => setShowRenameChannelModal(false)} className={''}>
        <Modal.Header closeButton />
        <Modal.Body>
          <p className={'message'}>{renameChannelErrorMessage}</p>
          <div className={'release-info-input channel-name'}>
            <label>Channel Name</label>
            <PlainTextInput
              placeholder={'Channel Name'}
              onChange={s => {
                setChannelName(s.slice(0, 200))
              }}
              value={channelName}
              className={'channel-name-input'}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={() => setShowRenameChannelModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => clickSubmitRenameChannel()} disabled={renamingChannel}>
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showDeleteChannelModal} onHide={() => setShowDeleteChannelModal(false)} className={''}>
        <Modal.Header closeButton />
        <Modal.Body>
          <p className={'message'}>{deleteChannelErrorMessage}</p>
          <div className={'release-info-delete-channel'}>Are you sure you want to delete {channelName}?</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" onClick={() => setShowDeleteChannelModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={() => clickSubmitDeleteChannel()} disabled={deletingChannel}>
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastComponent onClose={() => setToastMessage('')} show={!!toastMessage}>
        {toastMessage ?? ''}
      </ToastComponent>
    </div>
  )
}
